<template>
  <v-layout class="rights" row wrap style="flex: 1 0; overflow: auto; align-items: flex-start">
    <v-flex xs6 class="inline-block px-4">
      <v-card class="card--flex-toolbar">
        <v-toolbar card extended>
          <v-toolbar-title>{{ $t('Admin.User.Rights.GliRole') }}</v-toolbar-title>
          <template v-slot:extension>
            <v-autocomplete
              class="mr-2"
              :items="gliders"
              item-text="GLI_LIBELLE"
              item-value="GLI_ID"
              :label="$t('Admin.User.Rights.GliderToSelect')"
              v-model="gliId"
            ></v-autocomplete>
            <v-select
              :items="roles"
              item-text="ROL_LIBELLE"
              item-value="ROL_KEY"
              :label="$t('Admin.User.Rights.RoleToAdd')"
              v-model="gliRoleKey"
            ></v-select>
            <v-spacer></v-spacer>
            <v-btn :disabled="gliId == -1 || gliRoleKey == ''" @click="addGliderMappingRole">
              {{ $t('Admin.User.Rights.AddRole') }}
              <v-icon right>mdi-plus-circle</v-icon>
            </v-btn>
          </template>
        </v-toolbar>
        <v-data-table
          :headers="gliderRoleHeaders"
          :items="userGlidersRolesMapping"
          class="elevation-0"
          hide-actions
          :loading="specificGliderLoading"
          :no-data-text="$t('Admin.User.Rights.NoSpecificGliderRole')"
        >
          <template v-slot:items="props">
            <td>{{ glidersById[props.item.key].GLI_LIBELLE }}</td>
            <td class="text-xs-right">
              <v-chip
                v-for="mapping in props.item.values"
                :key="mapping.ROL_ID"
                close
                @input="removeMappingRole(mapping)"
              >
                {{ rolesByKey[mapping.ROL_KEY] && rolesByKey[mapping.ROL_KEY].ROL_LIBELLE }}
              </v-chip>
            </td>
          </template>
        </v-data-table>
      </v-card>
    </v-flex>
    <v-flex xs6 class="inline-block px-4">
      <v-card class="card--flex-toolbar">
        <v-toolbar card extended>
          <v-toolbar-title>{{ $t('Admin.User.Rights.MisRole') }}</v-toolbar-title>
          <v-autocomplete
            class="pl-2 mr-2"
            :items="gliders"
            item-text="GLI_LIBELLE"
            item-value="GLI_ID"
            :label="$t('Admin.User.Rights.GliderToSelect')"
            clearable
            v-model="gliFilterId"
          ></v-autocomplete>
          <template v-slot:extension>
            <v-autocomplete
              class="mr-2"
              :items="adminMissions"
              item-text="MIS_LIBELLE"
              item-value="MIS_ID"
              :label="$t('Admin.User.Rights.MissionToSelect')"
              v-model="misId"
            ></v-autocomplete>
            <v-select
              :items="roles"
              item-text="ROL_LIBELLE"
              item-value="ROL_KEY"
              :label="$t('Admin.User.Rights.RoleToAdd')"
              v-model="misRoleKey"
            ></v-select>
            <v-spacer></v-spacer>
            <v-btn :disabled="misId == -1 || misRoleKey == ''" @click="addMissionMappingRole">
              {{ $t('Admin.User.Rights.AddRole') }}
              <v-icon right>mdi-plus-circle</v-icon>
            </v-btn>
          </template>
        </v-toolbar>
        <v-data-table
          :headers="missionRoleHeaders"
          :items="userMissionsRolesMapping"
          class="elevation-0"
          hide-actions
          :loading="specificMissionLoading"
          :no-data-text="$t('Admin.User.Rights.NoSpecificMissionRole')"
        >
          <template v-slot:items="props">
            <td>{{ adminMissionsById[props.item.key] && adminMissionsById[props.item.key].MIS_LIBELLE }}</td>
            <td class="text-xs-right">
              <v-chip
                v-for="mapping in props.item.values"
                :key="mapping.ROL_ID"
                close
                @input="removeMappingRole(mapping)"
              >
                {{ rolesByKey[mapping.ROL_KEY] && rolesByKey[mapping.ROL_KEY].ROL_LIBELLE }}
              </v-chip>
            </td>
          </template>
        </v-data-table>
      </v-card>
    </v-flex>
  </v-layout>
</template>

<script lang="ts">
import Vue from 'vue'
import Component from 'vue-class-component'
import { Watch } from 'vue-property-decorator'
import { AloDbRoleMapping } from '../../../models/alodb/AloDbRoleMapping'
import { toGroupDictionnary } from '@/utils/groupBy'

@Component({
  name: 'AdminUserRights',
})
export default class AdminUserRights extends Vue {
  public gliId: number = -1
  public gliFilterId: number | null = null
  public gliRoleKey: string = ''
  public misId: number = -1
  public misRoleKey: string = ''
  public gliderRoleHeaders = [
    {
      text: 'Glider',
      align: 'left',
      sortable: false,
    },
    {
      text: 'Roles',
      align: 'right',
      sortable: false,
      width: '100%',
    },
  ]
  public missionRoleHeaders = [
    {
      text: 'Mission',
      align: 'left',
      sortable: false,
    },
    {
      text: 'Roles',
      align: 'right',
      sortable: false,
      width: '100%',
    },
  ]

  /*Cycle de vie*/
  created() {
    this.$store.myActions.ROLE_ENSURE_LIST()
    this.$store.myActions.GLIDER_ENSURE_LIST()
    this.$store.myActions.ADMINMISSION_ENSURE_LIST()
    if (this.usr_id) this.ensureRoleMappings()
  }

  ensureRoleMappings() {
    this.$store.myActions.USER_ENSURE_ROLEMAPPING({ itemId: this.usr_id, explicit: true })
  }

  addMissionMappingRole() {
    this.$store.myActions.USER_ADD_ROLEMAPPING_REQUEST({
      usrId: this.usr_id,
      mapObject: 'mission',
      mapId: this.misId,
      rolKey: this.misRoleKey,
    })
  }

  addGliderMappingRole() {
    this.$store.myActions.USER_ADD_ROLEMAPPING_REQUEST({
      usrId: this.usr_id,
      mapObject: 'glider',
      mapId: this.gliId,
      rolKey: this.gliRoleKey,
    })
  }

  removeMappingRole(mapping: AloDbRoleMapping) {
    this.$store.myActions.USER_REMOVE_ROLEMAPPING_REQUEST({
      usrId: mapping.USR_ID!,
      mapObject: mapping.MAP_OBJECT!,
      mapId: mapping.MAP_ID!,
      rolKey: mapping.ROL_KEY!,
    })
  }

  get userRolesMapping() {
    return toGroupDictionnary(
      (this.$store.myGetters.modifiedAdmUserRolesMapping as Array<AloDbRoleMapping>) ?? [],
      dicKey => dicKey?.MAP_OBJECT!,
      grpKey => grpKey?.MAP_ID?.toString()!,
    )
  }

  get userMissionsRolesMapping() {
    return this.userRolesMapping['mission'] || []
  }
  get userGlidersRolesMapping() {
    return this.userRolesMapping['glider'] || []
  }
  get userFleetsRolesMapping() {
    return this.userRolesMapping['fleet'] || []
  }
  get userEnseignesRolesMapping() {
    return this.userRolesMapping['enseigne'] || []
  }
  get userUsersRolesMapping() {
    return this.userRolesMapping['user'] || []
  }

  get original() {
    return this.$store.myGetters.originalAdmUser
  }

  get usr_id() {
    return this.original.USR_ID!
  }

  @Watch('usr_id')
  onUsrIdChanged(to: string, oldVafroml: string) {
    this.ensureRoleMappings()
  }

  get roles() {
    return this.$store.myGetters.roles
  }

  get rolesByKey() {
    return this.$store.myGetters.rolesByKey
  }

  get gliders() {
    return this.$store.myGetters.gliders
  }

  get glidersById() {
    return this.$store.myGetters.glidersById
  }

  get adminMissions() {
    return this.$store.myGetters.adminMissions.filter(m => !this.gliFilterId || m.GLI_ID == this.gliFilterId)
  }

  get adminMissionsById() {
    return this.$store.myGetters.adminMissionsById
  }

  get specificMissionLoading() {
    return this.$store.myGetters.roleMappingLoading || this.$store.myGetters.roleMappingLoadingByType('mission')
  }

  get specificGliderLoading() {
    return this.$store.myGetters.roleMappingLoading || this.$store.myGetters.roleMappingLoadingByType('glider')
  }
}
</script>
<style>
.rights .v-table__overflow,
.rights .v-datatable {
  overflow-x: hidden;
}
</style>
